import React, { forwardRef, useImperativeHandle, useRef } from 'react';
import {
    Box,
    Grid,
    Paper,
    Typography,
    ToggleButtonGroup,
    ToggleButton,
    Tooltip
} from '@mui/material';
import { Help as HelpIcon } from '@mui/icons-material';

const EvaluationSection = forwardRef(({
    jhaData,
    isReadOnly = false,
    availableOptions = ['YES', 'NO', 'N/A'],
    invalidValues = [],
    onSaveEvaluation,
    containerSx = {}
}, ref) => {
    const containerRef = useRef(null);
    const itemRefs = useRef({});

    useImperativeHandle(ref, () => ({
        scrollToValue: (value, options = {}) => {
            const element = itemRefs.current[value];

            if (element) {
                // Scroll element into view
                element.scrollIntoView({
                    behavior: options.behavior || 'smooth',
                    block: 'center'
                });

                // Highlight the target element
                element.style.transition = 'background-color 0.3s';
                element.style.backgroundColor = '#fff3e0';
                setTimeout(() => {
                    element.style.backgroundColor = '';
                }, 2000);
            }
        }
    }));

    const evaluationItems = jhaData?.stepOne?.evaluationOfWork || [];

    const handleOptionChange = (itemValue, newValue) => {
        if (isReadOnly || !onSaveEvaluation) return;
        onSaveEvaluation(itemValue, newValue || '');
    };

    if (!evaluationItems.length) {
        return (
            <Box sx={containerSx}>
                <Typography color="text.secondary" align="center" sx={{ py: 4 }}>
                    No evaluation items available
                </Typography>
            </Box>
        );
    }

    return (
        <Box
            ref={containerRef}
            sx={{
                maxHeight: 'calc(100vh - 460px)',
                overflow: 'auto',
                pb: 0.5,
                ...containerSx
            }}
        >
            <Grid container spacing={0.5}>
                {evaluationItems.map((item, index) => {
                    const isInvalid = invalidValues.includes(item.value);
                    const selectedValue = item.selected || null;

                    return (
                        <Grid item xs={12} sm={6} key={`${item.value}-${index}`}>
                            <Paper
                                ref={el => itemRefs.current[item.value] = el}
                                elevation={0}
                                sx={{
                                    p: 0.65,
                                    border: '1px solid',
                                    borderColor: isInvalid ? '#ff9800' : '#e0e0e0',
                                    backgroundColor: isInvalid ? '#fff3e0' : '#fafafa',
                                    borderLeft: isInvalid ? '3px solid #ff9800' : '1px solid #e0e0e0',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 0.65,
                                    '&:hover': {
                                        backgroundColor: isInvalid ? '#ffe0b2' : '#f5f5f5'
                                    }
                                }}
                            >
                                <Typography
                                    variant="body2"
                                    sx={{
                                        fontWeight: isInvalid ? 600 : 400,
                                        color: isInvalid ? '#e65100' : 'text.primary',
                                        fontSize: '0.725rem',
                                        lineHeight: 1.3,
                                        flex: 1,
                                        minWidth: 0
                                    }}
                                >
                                    {item.value}
                                </Typography>
                                <ToggleButtonGroup
                                    value={selectedValue}
                                    exclusive
                                    onChange={(e, newValue) => handleOptionChange(item.value, newValue)}
                                    size="small"
                                    disabled={isReadOnly}
                                    sx={{
                                        flexShrink: 0,
                                        '& .MuiToggleButton-root': {
                                            px: 2,
                                            py: 0.5,
                                            fontSize: '0.75rem',
                                            textTransform: 'none',
                                            border: '1px solid #bdbdbd',
                                            minHeight: 32,
                                            '&.Mui-selected': {
                                                fontWeight: 600,
                                                border: '1px solid',
                                                '&:hover': {
                                                    opacity: 0.9
                                                }
                                            },
                                            '&:not(.Mui-selected)': {
                                                color: '#666'
                                            }
                                        }
                                    }}
                                >
                                    {availableOptions.map(option => (
                                        <ToggleButton
                                            key={option}
                                            value={option}
                                            sx={{
                                                '&.Mui-selected': {
                                                    backgroundColor: option === 'YES' ? '#4caf50' : '#2196f3',
                                                    color: '#fff',
                                                    borderColor: option === 'YES' ? '#4caf50 !important' : '#2196f3 !important',
                                                    '&:hover': {
                                                        backgroundColor: option === 'YES' ? '#45a049' : '#1e88e5'
                                                    }
                                                }
                                            }}
                                        >
                                            {option}
                                        </ToggleButton>
                                    ))}
                                </ToggleButtonGroup>
                            </Paper>
                        </Grid>
                    );
                })}
            </Grid>
        </Box>
    );
});

EvaluationSection.displayName = 'EvaluationSection';

export default EvaluationSection;